import { useState } from 'react';
import { AlertCircle, CheckCircle2, Phone, BarChart, Bell, Settings, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function RingbaIntegration() {
  const [isConnected, setIsConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [accountId, setAccountId] = useState('');

  const handleConnect = (event: React.FormEvent) => {
    event.preventDefault();
    // Here you would typically send the API key and account ID to your backend
    // For this example, we'll just simulate a successful connection
    if (apiKey && accountId) {
      setIsConnected(true);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="flex-1 p-8">
        <header className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Ringba Integration</h1>
          <p className="text-gray-600">Connect your Ringba account to leverage advanced call tracking and analytics.</p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Connect to Ringba</CardTitle>
              <CardDescription>Enter your API credentials to establish the connection.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConnect} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key">API Key</Label>
                  <Input
                    id="api-key"
                    type="password"
                    placeholder="Enter your Ringba API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account-id">Account ID</Label>
                  <Input
                    id="account-id"
                    type="text"
                    placeholder="Enter your Ringba Account ID"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="environment">Environment</Label>
                  <Select defaultValue="production">
                    <SelectTrigger id="environment">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  {isConnected ? 'Connected' : 'Connect'} <Phone className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
            <CardFooter className="justify-between">
              <p className="text-sm text-gray-500">Need help finding your credentials?</p>
              <Button variant="outline">View Documentation</Button>
            </CardFooter>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent>
                {isConnected ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Not Connected
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integration Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <BarChart className="w-4 h-4 mr-2" />
                      Real-time Analytics
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Bell className="w-4 h-4 mr-2" />
                      Call Notifications
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Auto-optimization
                    </span>
                    <Switch />
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="mb-4 text-2xl font-semibold">How It Works</h2>
          <Tabs defaultValue="setup" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="setup">Setup</TabsTrigger>
              <TabsTrigger value="track">Track</TabsTrigger>
              <TabsTrigger value="analyze">Analyze</TabsTrigger>
            </TabsList>
            <TabsContent value="setup" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">1. Set Up Your Integration</h3>
              <p>
                Enter your Ringba API key and Account ID to establish the connection. Choose the appropriate environment
                for your needs.
              </p>
            </TabsContent>
            <TabsContent value="track" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">2. Start Tracking Calls</h3>
              <p>
                Once connected, Ringba will begin tracking your calls and providing detailed analytics on each
                interaction.
              </p>
            </TabsContent>
            <TabsContent value="analyze" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">3. Analyze and Optimize</h3>
              <p>
                Use Ringba's powerful analytics tools to gain insights into your call performance and optimize your
                campaigns for better results.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <aside className="hidden w-64 p-6 bg-white border-l border-gray-200 lg:block">
        <h2 className="mb-4 text-lg font-semibold">Quick Links</h2>
        <nav className="space-y-2">
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Ringba Dashboard
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Campaign Manager
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Report Builder
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </nav>
        <div className="mt-8">
          <Badge variant="outline" className="mb-2">
            Pro Tip
          </Badge>
          <p className="text-sm text-gray-600">
            Use Ringba's API to automate your call routing and improve response times.
          </p>
        </div>
      </aside>
    </div>
  );
}
